import React, { useEffect, useState } from 'react';
import { Save, AlertTriangle, Info, Scale, CheckCircle } from 'lucide-react';
import { getSaldoAwal, saveSaldoAwal } from '../../api';

export default function SaldoAwal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [keterangan, setKeterangan] = useState('Saldo Awal');
  const [saldo, setSaldo] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    getSaldoAwal()
      .then(res => {
        const d = res.data || {};
        setData(d);
        setSaldo(d.saldo || []);
        if (d.tanggal) setTanggal(d.tanggal);
      })
      .catch(err => {
        console.error(err);
        setError('Gagal memuat data saldo awal.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateSaldo = (akunId, field, value) => {
    const n = Math.max(0, Number(value) || 0);
    setSaldo(prev => prev.map(s => s.akun_id === akunId ? { ...s, [field]: n } : s));
    setSuccess('');
  };

  const totalDebit = saldo.reduce((s, x) => s + (Number(x.debit) || 0), 0);
  const totalKredit = saldo.reduce((s, x) => s + (Number(x.kredit) || 0), 0);
  const selisih = totalDebit - totalKredit;
  const balanceOk = Math.abs(selisih) < 0.01;
  const adaNilai = saldo.some(x => (Number(x.debit) || 0) > 0 || (Number(x.kredit) || 0) > 0);

  const rupiah = (val) => {
    const n = Number(val) || 0;
    return `Rp ${n.toLocaleString('id-ID')}`;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!tanggal) { setError('Tanggal saldo awal wajib diisi.'); return; }
    if (!adaNilai) { setError('Minimal 1 akun dengan nilai saldo.'); return; }
    if (!balanceOk) { setError(`Total debit harus sama dengan total kredit (selisih: ${rupiah(Math.abs(selisih))}).`); return; }

    setSaving(true);
    try {
      const items = saldo
        .filter(s => (Number(s.debit) || 0) > 0 || (Number(s.kredit) || 0) > 0)
        .map(s => ({ akun_id: s.akun_id, debit: Number(s.debit) || 0, kredit: Number(s.kredit) || 0 }));
      const res = await saveSaldoAwal({ tanggal, keterangan: keterangan || 'Saldo Awal', items });
      setSuccess(`Saldo awal disimpan sebagai jurnal pembuka ${res.data?.no_jurnal || ''} — sudah masuk ke Neraca, Laba Rugi, dan Buku Besar.`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan saldo awal.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>Input Saldo Awal</h2>
          <p className="page-desc">Catat saldo awal setiap akun untuk memulai pembukuan</p>
        </div>
        <div className="page-loading"><div className="spinner-lg" /><p>Memuat daftar akun...</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Input Saldo Awal</h2>
        <p className="page-desc">Catat saldo awal setiap akun — tersimpan sebagai jurnal pembuka dan otomatis masuk ke Neraca, Laba Rugi, & Buku Besar</p>
      </div>

      {data?.sudah_diinput && !success && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem' }}>
          <Info size={16} style={{ marginTop: 2, flexShrink: 0 }} /><span>Saldo awal sudah pernah diinput — menyimpan akan memperbarui jurnal pembuka yang lama.</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ margin: 0, minWidth: 170 }}>
          <label style={{ fontSize: '0.75rem', marginBottom: 4, color: 'var(--gray-400)' }}>Tanggal Saldo Awal</label>
          <input type="date" value={tanggal} onChange={e => setTanggal(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
        </div>
        <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
          <label style={{ fontSize: '0.75rem', marginBottom: 4, color: 'var(--gray-400)' }}>Keterangan</label>
          <input type="text" value={keterangan} onChange={e => setKeterangan(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Save size={16} />{saving ? 'Menyimpan...' : (data?.sudah_diinput ? 'Simpan & Perbarui' : 'Simpan Saldo Awal')}
        </button>
      </div>

      {success && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#f0fdf4', color: '#065f46', border: '1px solid #bbf7d0', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem' }}>
          <CheckCircle size={16} style={{ marginTop: 2, flexShrink: 0 }} /><span>{success}</span>
        </div>
      )}
      {error && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem' }}>
          <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} /><span>{error}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#f0f9ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem' }}>
        <Info size={16} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>
          Isi nilai saldo sesuai <strong>saldo normal</strong> akun: akun ber-saldo normal <strong>Debit</strong> (Aset, Beban) diisi di kolom Debit; akun ber-saldo normal <strong>Kredit</strong> (Kewajiban, Modal, Pendapatan) diisi di kolom Kredit.
          Total Debit harus sama dengan Total Kredit.
        </span>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Kode</th>
              <th>Nama Akun</th>
              <th style={{ width: 110 }}>Tipe</th>
              <th style={{ width: 110 }}>Saldo Normal</th>
              <th style={{ width: 180, textAlign: 'right' }}>Debit (Rp)</th>
              <th style={{ width: 180, textAlign: 'right' }}>Kredit (Rp)</th>
            </tr>
          </thead>
          <tbody>
            {saldo.map(s => (
              <tr key={s.akun_id}>
                <td><code>{s.kode_akun}</code></td>
                <td><strong style={{ fontSize: '0.85rem' }}>{s.nama_akun}</strong></td>
                <td><span className="badge badge-default">{s.tipe}</span></td>
                <td style={{ fontSize: '0.8rem', color: s.saldo_normal === 'Debit' ? '#065f46' : '#92400e' }}>
                  {s.saldo_normal}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <input type="number" min="0" step="0.01" value={s.debit || ''} placeholder="0"
                    onChange={e => updateSaldo(s.akun_id, 'debit', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <input type="number" min="0" step="0.01" value={s.kredit || ''} placeholder="0"
                    onChange={e => updateSaldo(s.akun_id, 'kredit', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--gray-50)', fontWeight: 700 }}>
              <td colSpan={4} style={{ textAlign: 'right', fontSize: '0.85rem' }}>TOTAL</td>
              <td style={{ textAlign: 'right', fontSize: '0.85rem', color: '#065f46' }}>{rupiah(totalDebit)}</td>
              <td style={{ textAlign: 'right', fontSize: '0.85rem', color: '#92400e' }}>{rupiah(totalKredit)}</td>
            </tr>
            <tr style={{ background: balanceOk ? '#f0fdf4' : '#fef2f2' }}>
              <td colSpan={4} style={{ textAlign: 'right', fontSize: '0.82rem', color: balanceOk ? '#065f46' : '#991b1b' }}>
                <Scale size={13} style={{ verticalAlign: '-2px' }} /> {balanceOk ? 'Balance ✓' : 'Selisih:'}
              </td>
              <td colSpan={2} style={{ textAlign: 'right', fontSize: '0.82rem', fontWeight: 700, color: balanceOk ? '#065f46' : '#991b1b' }}>
                {balanceOk ? 'Debit = Kredit' : rupiah(Math.abs(selisih))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}